<template>
  <div v-if="initPage">
    <div v-loading="true" class="loading"></div>
  </div>
  <div v-else>
    <el-row type="flex" justify="space-between">
      <div class="info-filter">
        <span v-if="bankPayFilter.period.enabled">
          {{ $t("common.filter.info.periodWith") }}
          {{ bankPayFilter.period.dateBegin }}
          {{ $t("common.filter.info.periodTo") }}
          {{ bankPayFilter.period.dateEnd }}
        </span>
        <span v-if="bankPayFilter.sum.enabled">
          {{ $t("common.filter.info.sumMin") }}
          {{ bankPayFilter.sum.sumBegin }} {{ params.goptions.monetary_unit }},
          {{ $t("common.filter.info.sumMax") }} {{ bankPayFilter.sum.sumEnd }}
          {{ params.goptions.monetary_unit }}
        </span>
        <span v-if="bankPayFilter.statement.enabled">
          {{
            bankPayFilter.statement.status === "1"
              ? $t("common.filter.info.statementSpaced")
              : $t("common.filter.info.statementSet")
          }}
        </span>
      </div>
      <el-button
        :type="filterType"
        size="small"
        @click="filterModal = true"
        v-shortkey="{ F7: ['f7'] }"
        @shortkey.native="onShortKeyRouter"
        ><span class="key-name">F7</span
        >{{ $t("common.buttons.filter") }}</el-button
      >
    </el-row>
    <el-row type="flex" justify="space-between">
      <el-col :span="24" class="grid-container">
        <ag-grid-vue
          style="width: 100%; height: 100%; text-align: center; font-family: 'Open Sans', sans-serif;"
          class="ag-theme-balham"
          :columnDefs="bankStatColumnDefs"
          :rowData="bankStat"
          :defaultColDef="defaultColDef"
          :gridOptions="bankStatGridOptions"
          rowSelection="single"
          @grid-ready="onBankStatGridReady"
          @first-data-rendered="onBankStatFirstDataRendered"
          @gridSizeChanged="onGridSizeChanged"
          @modelUpdated="onBankStatModelUpdated"
          @rowSelected="onBankStatRowSelected"
          @cellContextMenu="onBankStatRowRightClicked"
        ></ag-grid-vue>
      </el-col>
    </el-row>
    <el-row type="flex" justify="space-between">
      <el-col :span="24" class="grid-container">
        <ag-grid-vue
          style="width: 100%; height: 100%; text-align: center; font-family: 'Open Sans', sans-serif;"
          class="ag-theme-balham"
          :columnDefs="payDocColumnDefs"
          :rowData="payDoc"
          :defaultColDef="defaultColDef"
          :gridOptions="payDocGridOptions"
          :getRowStyle="getRowStyle"
          :pinnedBottomRowData="payDocPinnedBottomRowData"
          :modules="agGridModules"
          rowSelection="single"
          @grid-ready="onPayDocGridReady"
          @first-data-rendered="onPayDocFirstDataRendered"
          @gridSizeChanged="onGridSizeChanged"
          @modelUpdated="onPayDocModelUpdated"
          @cellContextMenu="onPayDocRowRightClicked"
        ></ag-grid-vue>
      </el-col>
    </el-row>
    <el-row type="flex" justify="space-between" style="margin-top: 10px">
      <el-col :span="24">
        <el-button-group style="margin-right: 20px; margin-top: -1px;">
          <el-button
            type="primary"
            size="small"
            :disabled="addBtnDisabled"
            @click="onAddBtn"
            v-shortkey="{ Insert: ['insert'], Plus: ['+'] }"
            @shortkey.native="onShortKeyRouter"
            ><span class="key-name">Ins</span
            >{{ $t("common.buttons.add") }}</el-button
          >
          <el-button
            type="primary"
            size="small"
            :disabled="editBtnDisabled"
            @click="onEditBtn"
            v-shortkey="{ F4: ['f4'], Star: ['*'] }"
            @shortkey.native="onShortKeyRouter"
            ><span class="key-name">F4</span
            >{{ $t("common.buttons.edit") }}</el-button
          >
          <el-button
            type="primary"
            size="small"
            :disabled="deleteBtnDisabled"
            @click="onDeleteBtn"
            v-shortkey="{ Del: ['del'], Minus: ['-'] }"
            @shortkey.native="onShortKeyRouter"
            ><span class="key-name">Del</span
            >{{ $t("common.buttons.delete") }}</el-button
          >
        </el-button-group>
        <el-button
          type="primary"
          size="small"
          :disabled="importBtnDisabled"
          @click="onImportBtn"
          v-shortkey="{ F8: ['f8'] }"
          @shortkey.native="onShortKeyRouter"
          ><span class="key-name">F8</span
          >{{ $t("common.buttons.import") }}</el-button
        >
      </el-col>
    </el-row>

    <el-dialog
      :title="$t('common.filter.modal.title')"
      :visible.sync="filterModal"
      :close-on-click-modal="false"
      @open="filterModalShow"
      @close="filterModalHide"
      :width="dialogWidth"
      class="resize"
      center
      v-draggable
    >
      <el-form :model="filterForm" :rules="filterRules" ref="filterForm">
        <el-row type="flex" justify="space-between">
          <el-col :span="1">
            <el-form-item>
              <el-checkbox
                v-model="filterForm.dateEnabled"
                disabled
              ></el-checkbox>
            </el-form-item>
          </el-col>
          <el-col :span="5">
            <el-form-item>
              <div>{{ $t("common.filter.form.dateRange.title") }}</div>
            </el-form-item>
          </el-col>
          <el-col :span="1">
            <el-form-item>
              <div style="text-align: center">
                {{ $t("common.filter.form.dateRange.from") }}
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="17">
            <el-form-item prop="dateRange">
              <el-date-picker
                v-model="filterForm.dateRange"
                @keydown.f10.native="e => e.preventDefault()"
                type="daterange"
                :start-placeholder="$t('common.filter.form.dateRange.start')"
                :end-placeholder="$t('common.filter.form.dateRange.end')"
                :range-separator="$t('common.filter.form.dateRange.separator')"
                format="dd.MM.yyyy"
                value-format="dd.MM.yyyy"
                unlink-panels
                style="width: 100%;"
              ></el-date-picker>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row type="flex" justify="space-between">
          <el-col :span="1">
            <el-form-item>
              <el-checkbox v-model="filterForm.sumEnabled"></el-checkbox>
            </el-form-item>
          </el-col>
          <el-col :span="5">
            <el-form-item>
              <div>{{ $t("common.filter.form.sumRange.title") }}</div>
            </el-form-item>
          </el-col>
          <el-col :span="1">
            <el-form-item>
              <div style="text-align: center">
                {{ $t("common.filter.form.sumRange.from") }}
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item prop="sumRange">
              <currency-input
                placeholder="0.00"
                v-model.lazy="filterForm.sumBegin"
                @keydown.f10.native="e => e.preventDefault()"
                :disabled="!filterForm.sumEnabled"
                class="el-input--small el-input__inner"
                :class="{ moneydisable: !filterForm.sumEnabled }"
                style="width: 100%; height: 36px; line-height: 36px;"
              />
            </el-form-item>
          </el-col>
          <el-col :span="1">
            <el-form-item>
              <div style="text-align: center">
                {{ $t("common.filter.form.sumRange.to") }}
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item>
              <currency-input
                placeholder="0.00"
                v-model.lazy="filterForm.sumEnd"
                @keydown.f10.native="e => e.preventDefault()"
                :disabled="!filterForm.sumEnabled"
                class="el-input--small el-input__inner"
                :class="{ moneydisable: !filterForm.sumEnabled }"
                style="width: 100%; height: 36px; line-height: 36px;"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row type="flex" justify="space-between">
          <el-col :span="1">
            <el-form-item>
              <el-checkbox v-model="filterForm.statementEnabled"></el-checkbox>
            </el-form-item>
          </el-col>
          <el-col :span="5">
            <el-form-item>
              <div>{{ $t("common.filter.form.statementStatus.title") }}</div>
            </el-form-item>
          </el-col>
          <el-col :span="1">
            <el-form-item>
              <div>&nbsp;</div>
            </el-form-item>
          </el-col>
          <el-col :span="17">
            <el-form-item prop="statementStatus">
              <el-select
                v-model="filterForm.statementStatus"
                @keydown.f10.native="e => e.preventDefault()"
                :placeholder="$t('common.components.select.placeholder')"
                clearable
                filterable
                :disabled="!filterForm.statementEnabled"
                style="width: 100%;"
              >
                <el-option
                  v-for="item in filterStatusOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-row type="flex" justify="space-between">
          <el-col :span="24" style="text-align:right;">
            <el-button
              type="success"
              size="small"
              @click="filterModalSubmit"
              native-type="submit"
              v-shortkey="{ F10: ['f10'] }"
              @shortkey.native="onShortKeyRouter"
              ><span class="key-name">F10</span
              >{{ $t("common.buttons.submit") }}</el-button
            >
            <el-button type="plain" size="small" @click="filterModal = false"
              ><span class="key-name">Esc</span
              >{{ $t("common.buttons.cancel") }}</el-button
            >
          </el-col></el-row
        >
      </span>
    </el-dialog>

    <el-dialog
      :title="$t('bankpay.bankStat.modal.title')"
      :visible.sync="bankStatModal"
      :close-on-click-modal="false"
      @open="bankStatModalShow"
      @close="bankStatModalHide"
      :width="largeDialogWidth"
      top="1em"
      v-draggable
    >
      <el-dialog
        :title="$t('bankpay.payDocEdit.modal.title')"
        :visible.sync="payDocEditModal"
        :close-on-click-modal="false"
        @open="payDocEditModalShow"
        @close="payDocEditModalHide"
        :width="largeDialogWidth"
        top="2em"
        append-to-body
        v-draggable
      >
        <el-form :model="payDocForm" :rules="payDocRules" ref="payDocForm">
          <el-row type="flex" justify="space-between">
            <el-col :span="24">
              <el-form-item
                prop="paymentStatus"
                :label="$t('bankpay.payDocEdit.form.paymentStatus.title')"
                label-width="150px"
              >
                <el-select
                  v-model="payDocForm.paymentStatus"
                  @keydown.f10.native="e => e.preventDefault()"
                  :placeholder="$t('common.components.select.placeholder')"
                  clearable
                  filterable
                  style="width: 100%;"
                  v-shortkey="{ F2: ['f2'] }"
                  @shortkey.native="onShortKeyRouter"
                >
                  <el-option
                    v-for="item in paymentOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" justify="space-between">
            <el-col :span="12">
              <el-form-item
                prop="paynumber"
                :label="$t('bankpay.payDocEdit.form.paynumber.title')"
                label-width="150px"
              >
                <el-input
                  v-model="payDocForm.paynumber"
                  @keydown.f10.native="e => e.preventDefault()"
                  clearable
                  style="width: 100%;"
                ></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12" style="text-align:right;">
              <el-form-item
                :label="$t('bankpay.payDocEdit.form.okpo.title')"
                label-width="150px"
              >
                <el-input
                  v-model="payDocForm.okpo"
                  @keydown.f10.native="e => e.preventDefault()"
                  clearable
                  style="width: 100%;"
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" justify="space-between">
            <el-col :span="24">
              <el-form-item
                prop="datepay"
                :label="$t('bankpay.payDocEdit.form.datepay.title')"
                label-width="150px"
              >
                <el-date-picker
                  v-model="payDocForm.datepay"
                  @keydown.f10.native="e => e.preventDefault()"
                  :disabled="payDocDatepayDisabled"
                  type="date"
                  :placeholder="$t('common.components.datePicker.placeholder')"
                  format="dd.MM.yyyy"
                  value-format="dd.MM.yyyy"
                  :picker-options="pickerOptions"
                ></el-date-picker>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" justify="space-between">
            <el-col :span="24">
              <el-form-item
                prop="codefirme"
                :label="$t('bankpay.payDocEdit.form.codefirme.title')"
                label-width="150px"
              >
                <el-row type="flex" justify="space-between" :gutter="5">
                  <el-col :span="4">
                    <el-input
                      v-model="payDocForm.mfo"
                      @keydown.f10.native="e => e.preventDefault()"
                      clearable
                      style="width: 100%;"
                      @input="onChangeMfo"
                      :disabled="payDocSenderDisabled"
                    ></el-input>
                  </el-col>
                  <el-col :span="2">
                    <currency-input
                      placeholder=""
                      v-model="payDocForm.codefirmeCode"
                      @blur="onChangeCodefirmeCode"
                      @keyup.enter.native="onChangeCodefirmeCode"
                      @keydown.f10.native="e => e.preventDefault()"
                      :precision="0"
                      :disabled="payDocSenderDisabled"
                      class="el-input--small el-input__inner"
                      :class="{ moneydisable: payDocSenderDisabled }"
                      style="width: 100%; height: 37px; line-height: 37px;"
                    />
                  </el-col>
                  <el-col :span="18">
                    <el-select
                      v-model="payDocForm.codefirme"
                      @keydown.f10.native="e => e.preventDefault()"
                      :placeholder="$t('common.components.select.placeholder')"
                      clearable
                      filterable
                      style="width: 100%;"
                      :disabled="payDocSenderDisabled"
                    >
                      <el-option
                        v-for="item in payDocForm.senders"
                        :key="item.codefirme"
                        :label="item.namefirme"
                        :value="item.codefirme"
                      ></el-option>
                    </el-select>
                  </el-col>
                </el-row>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" justify="space-between">
            <el-col :span="24">
              <el-form-item
                prop="summa"
                :label="$t('bankpay.payDocEdit.form.summa.title')"
                label-width="150px"
              >
                <currency-input
                  placeholder="0.00"
                  v-model.lazy="payDocForm.summa"
                  @keydown.f10.native="e => e.preventDefault()"
                  :disabled="payDocSummaDisabled"
                  class="el-input--small el-input__inner"
                  :class="{ moneydisable: payDocSummaDisabled }"
                  style="width: 25%; height: 36px; line-height: 36px; margin-right: 10px;"
                />
                <el-checkbox
                  v-model="payDocForm.astatus"
                  v-shortkey="{ F3: ['f3'] }"
                  @shortkey.native="onShortKeyRouter"
                  >F3 - {{ $t("bankpay.payDocEdit.form.astatus.title") }}
                </el-checkbox>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" justify="space-between">
            <el-col :span="24">
              <el-form-item
                prop="kindpay"
                :label="$t('bankpay.payDocEdit.form.kindpay.title')"
                label-width="150px"
              >
                <el-row type="flex" justify="space-between" :gutter="5">
                  <el-col :span="2">
                    <currency-input
                      placeholder=""
                      v-model="payDocForm.kindpayCode"
                      @blur="onChangeKindpayCode"
                      @keyup.enter.native="onChangeKindpayCode"
                      @keydown.f10.native="e => e.preventDefault()"
                      :precision="0"
                      class="el-input--small el-input__inner"
                      style="width: 100%; height: 37px; line-height: 37px;"
                    />
                  </el-col>

                  <el-col :span="22">
                    <el-select
                      v-model="payDocForm.kindpay"
                      @keydown.f10.native="e => e.preventDefault()"
                      :placeholder="$t('common.components.select.placeholder')"
                      clearable
                      filterable
                      style="width: 100%;"
                    >
                      <el-option
                        v-for="item in payDocForm.kindpays"
                        :key="item.kindpay"
                        :label="item.kindname"
                        :value="item.kindpay"
                      ></el-option>
                    </el-select>
                  </el-col>
                </el-row>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" justify="space-between">
            <el-col :span="24">
              <el-form-item
                :label="$t('bankpay.payDocEdit.form.mess.title')"
                label-width="150px"
              >
                <el-input
                  v-model="payDocForm.mess"
                  @keydown.f10.native="e => e.preventDefault()"
                  clearable
                  style="width: 100%;"
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>

        <span slot="footer" class="dialog-footer">
          <el-row type="flex" justify="space-between">
            <el-col :span="12" style="text-align:left;">
              <el-button
                v-if="payDocEditStatus == 'insert'"
                type="primary"
                size="small"
                @click="payDocFormAdd"
                native-type="submit"
                v-shortkey="{
                  Insert: ['insert'],
                  Plus: ['+']
                }"
                @shortkey.native="onShortKeyRouter"
                ><span class="key-name">Ins</span
                >{{ $t("common.buttons.add") }}</el-button
              >
            </el-col>
            <el-col :span="12" style="text-align:right;">
              <el-button
                type="primary"
                size="small"
                @click="payDocFormSubmit"
                native-type="submit"
                v-shortkey="{ F10: ['f10'] }"
                @shortkey.native="onShortKeyRouter"
                ><span class="key-name">F10</span
                >{{ $t("common.buttons.submit") }}</el-button
              >
              <el-button
                type="plain"
                size="small"
                @click="payDocEditModal = false"
                ><span class="key-name">Esc</span
                >{{ $t("common.buttons.cancel") }}</el-button
              >
            </el-col>
          </el-row>
        </span>
      </el-dialog>

      <el-dialog
        width="35%"
        :title="$t('bankpay.uploadFiles.modal.title')"
        :visible.sync="uploadFilesModal"
        :close-on-click-modal="false"
        @open="uploadFilesModalShow"
        @close="uploadFilesModalHide"
        append-to-body
        center
        v-draggable
      >
        <el-form :model="uploadForm" :rules="uploadRules" ref="uploadForm">
          <el-row type="flex" justify="space-between">
            <el-col :span="24">
              <el-form-item
                prop="encoding"
                :label="$t('bankpay.uploadFiles.form.encoding.title')"
                label-width="160px"
              >
                <el-select
                  v-model="uploadForm.encoding"
                  @keydown.f10.native="e => e.preventDefault()"
                  :placeholder="$t('common.components.select.placeholder')"
                  clearable
                  filterable
                  style="width: 100%;"
                >
                  <el-option
                    v-for="item in params.importcodepage"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
              </el-form-item>
              <el-form-item
                prop="uploadfile"
                :label="$t('bankpay.uploadFiles.form.uploadfile.title')"
              >
                <el-upload
                  action
                  ref="upload"
                  :accept="fileMask"
                  :auto-upload="false"
                  :multiple="multiupload"
                >
                  <el-button
                    slot="trigger"
                    size="small"
                    type="primary"
                    icon="fas fa-folder"
                    >{{ $t("common.buttons.selectFile") }}</el-button
                  >
                </el-upload>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>

        <span slot="footer" class="dialog-footer">
          <el-row type="flex" justify="space-between">
            <el-col :span="24" style="text-align:right;">
              <el-button
                type="primary"
                size="small"
                icon="fas fa-upload"
                @click="uploadFilesSubmit"
                >{{ $t("common.buttons.upload") }}</el-button
              >
              <el-button
                type="plain"
                size="small"
                icon="fas fa-times"
                @click="uploadFilesModal = false"
                >{{ $t("common.buttons.cancel") }}</el-button
              >
            </el-col>
          </el-row>
        </span>
      </el-dialog>

      <el-dialog
        :title="$t('bankpay.confirm.modal.title')"
        :visible.sync="confirmModal"
        :close-on-click-modal="false"
        @open="confirmModalShow"
        @close="confirmModalHide"
        :width="largeDialogWidth"
        top="2em"
        append-to-body
        v-draggable
      >
        <el-form :model="confirmForm" :rules="confirmRules" ref="confirmForm">
          <el-row type="flex" justify="space-between">
            <el-col :span="24">
              <el-form-item>
                <div>
                  {{ $t("bankpay.confirm.form.info.title") }}
                </div>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" justify="space-between">
            <el-col :span="24">
              <el-form-item
                :label="$t('bankpay.confirm.form.sender.title')"
                label-width="150px"
              >
                <el-input
                  v-model="confirmForm.name"
                  @keydown.f10.native="e => e.preventDefault()"
                  :disabled="true"
                  clearable
                  style="width: 100%;"
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" justify="space-between">
            <el-col :span="12">
              <el-form-item
                :label="$t('bankpay.confirm.form.mfo.title')"
                label-width="150px"
              >
                <el-input
                  v-model="confirmForm.mfo"
                  @keydown.f10.native="e => e.preventDefault()"
                  :disabled="true"
                  clearable
                  style="width: 100%;"
                ></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12" style="text-align:right;">
              <el-form-item
                :label="$t('bankpay.confirm.form.rcount.title')"
                label-width="150px"
              >
                <el-input
                  v-model="confirmForm.rcount"
                  @keydown.f10.native="e => e.preventDefault()"
                  :disabled="true"
                  clearable
                  style="width: 100%;"
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" justify="space-between">
            <el-col :span="12">
              <el-form-item
                :label="$t('bankpay.confirm.form.summa.title')"
                label-width="150px"
              >
                <currency-input
                  placeholder="0.00"
                  v-model.lazy="confirmForm.summa"
                  @keydown.f10.native="e => e.preventDefault()"
                  :disabled="false"
                  class="el-input--small el-input__inner"
                  :class="{ moneydisable: true }"
                  style="width: 100%; height: 36px; line-height: 36px; margin-right: 10px;"
                />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item
                :label="$t('bankpay.confirm.form.okpo.title')"
                label-width="150px"
              >
                <el-input
                  v-model="confirmForm.okpo"
                  @keydown.f10.native="e => e.preventDefault()"
                  :disabled="true"
                  clearable
                  style="width: 100%;"
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" justify="space-between">
            <el-col :span="24">
              <el-form-item
                prop="codefirme"
                :label="$t('bankpay.confirm.form.codefirme.title')"
                label-width="150px"
              >
                <el-select
                  v-model="confirmForm.codefirme"
                  @keydown.f10.native="e => e.preventDefault()"
                  :disabled="confirmForm.codefirmeDisabled"
                  :placeholder="$t('common.components.select.placeholder')"
                  clearable
                  filterable
                  style="width: 100%;"
                >
                  <el-option
                    v-for="item in confirmForm.organizations"
                    :key="item.codefirme"
                    :label="item.namefirme"
                    :value="item.codefirme"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row type="flex" justify="space-between">
            <el-col :span="24">
              <el-form-item>
                <div>
                  <el-checkbox v-model="confirmForm.alienpay">{{
                    $t("bankpay.confirm.form.alienpay.title")
                  }}</el-checkbox>
                </div>
                <div>
                  <el-checkbox v-model="confirmForm.saveforsender">{{
                    $t("bankpay.confirm.form.saveforsender.title")
                  }}</el-checkbox>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>

        <span slot="footer" class="dialog-footer">
          <el-row type="flex" justify="space-between">
            <el-col :span="24" style="text-align:right;">
              <el-button
                type="primary"
                size="small"
                icon="fas fa-check"
                @click="confirmFormSubmit"
                native-type="submit"
                >{{ $t("common.buttons.submit") }}</el-button
              >
              <el-button
                type="plain"
                size="small"
                icon="fas fa-times"
                @click="confirmModal = false"
                >{{ $t("common.buttons.cancel") }}</el-button
              >
            </el-col>
          </el-row>
        </span>
      </el-dialog>

      <el-form :model="payBankForm" :rules="payBankRules" ref="payBankForm">
        <el-row type="flex" justify="space-between">
          <el-col :span="12">
            <el-form-item
              prop="datebank"
              :label="$t('bankpay.bankStat.form.datebank.title')"
              label-width="180px"
            >
              <el-date-picker
                v-model="payBankForm.datebank"
                @keydown.f10.native="e => e.preventDefault()"
                type="date"
                :placeholder="$t('common.components.datePicker.placeholder')"
                format="dd.MM.yyyy"
                value-format="dd.MM.yyyy"
                :picker-options="pickerOptions"
              ></el-date-picker>
            </el-form-item>
          </el-col>
          <el-col :span="12"></el-col>
        </el-row>
        <el-row type="flex" justify="space-between">
          <el-col :span="12">
            <el-form-item
              prop="quantity"
              :label="$t('bankpay.bankStat.form.quantity.title')"
              label-width="180px"
            >
              <el-input-number
                v-model.lazy="payBankForm.quantity"
                @keydown.f10.native="e => e.preventDefault()"
                :disabled="payBankForm.quantityDisabled"
                :precision="0"
                :controls="false"
                :min="0"
                :max="65535"
                style="width: 90%;"
                size="small"
              ></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12" style="text-align:right;">
            <el-form-item
              prop="summa"
              :label="$t('bankpay.bankStat.form.summa.title')"
              label-width="180px"
            >
              <currency-input
                placeholder="0.00"
                v-model.lazy="payBankForm.summa"
                @keydown.f10.native="e => e.preventDefault()"
                :disabled="payBankForm.summaDisabled"
                class="el-input--small el-input__inner"
                :class="{ moneydisable: payBankForm.summaDisabled }"
                style="width: 90%; height: 36px; line-height: 36px"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row type="flex" justify="space-between">
          <el-col :span="24">
            <el-form-item
              prop="accountid"
              :label="$t('bankpay.bankStat.form.accountid.title')"
              label-width="180px"
            >
              <el-row type="flex" justify="space-between" :gutter="5">
                <el-col :span="2">
                  <currency-input
                    placeholder=""
                    v-model="payBankForm.accountidCode"
                    @blur="onChangeAccountidCode"
                    @keyup.enter.native="onChangeAccountidCode"
                    @keydown.f10.native="e => e.preventDefault()"
                    :precision="0"
                    class="el-input--small el-input__inner"
                    style="width: 100%; height: 37px; line-height: 37px;"
                  />
                </el-col>
                <el-col :span="22">
                  <el-select
                    v-model="payBankForm.accountid"
                    @keydown.f10.native="e => e.preventDefault()"
                    :placeholder="$t('common.components.select.placeholder')"
                    clearable
                    filterable
                    style="width: 100%;"
                  >
                    <el-option
                      v-for="item in payBankForm.accounts"
                      :key="item.accountid"
                      :label="`${item.namebank} | ${item.rcount}`"
                      :value="item.accountid"
                    ></el-option>
                  </el-select>
                </el-col>
              </el-row>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row type="flex" justify="space-between">
          <el-col :span="24">
            <el-button-group v-if="importModalBtnVisible" class="button-group">
              <el-dropdown trigger="click">
                <el-button
                  type="primary"
                  size="small"
                  icon="fas fa-file-upload"
                  class="buttons-panel"
                >
                  {{ $t("common.buttons.importDots.import") }}
                  <i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item @click.native="onImportModalDbfBtn">{{
                    $t("common.buttons.importDots.dbfFile")
                  }}</el-dropdown-item>
                  <el-dropdown-item @click.native="onImportModalCsvBtn">{{
                    $t("common.buttons.importDots.csvFile")
                  }}</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </el-button-group>
            <el-button-group class="button-group">
              <el-button
                v-if="addModalBtnVisible"
                type="primary"
                size="small"
                @click="onAddModalBtn"
                v-shortkey="{
                  Insert: ['insert'],
                  Plus: ['+']
                }"
                @shortkey.native="onShortKeyRouter"
                ><span class="key-name">Ins</span
                >{{ $t("common.buttons.add") }}</el-button
              >
              <el-button
                type="primary"
                size="small"
                @click="onEditModalBtn"
                :disabled="editModalBtnDisabled"
                v-shortkey="{ F4: ['f4'], Star: ['*'] }"
                @shortkey.native="onShortKeyRouter"
                ><span class="key-name">F4</span
                >{{ $t("common.buttons.edit") }}</el-button
              >
              <el-button
                v-if="deleteModalBtnVisible"
                type="primary"
                size="small"
                @click="onDeletModalBtn"
                :disabled="deleteModalBtnDisabled"
                v-shortkey="{
                  Del: ['del'],
                  Minus: ['-']
                }"
                @shortkey.native="onShortKeyRouter"
                ><span class="key-name">Del</span
                >{{ $t("common.buttons.remove") }}</el-button
              >
            </el-button-group>
            <el-button-group class="button-group">
              <el-button
                type="primary"
                size="small"
                icon="fas fa-times-circle"
                @click="onAlienModalBtn"
                :disabled="alienModalBtnDisabled"
                >{{ $t("common.buttons.alien") }}</el-button
              >
            </el-button-group>
          </el-col>
        </el-row>
        <el-row type="flex" justify="space-between">
          <el-col :span="24" class="grid-container-modal">
            <ag-grid-vue
              style="width: 100%; height: 100%; text-align: center; font-family: 'Open Sans', sans-serif;"
              class="ag-theme-balham"
              :columnDefs="payDocModalColumnDefs"
              :rowData="payDocModal"
              :defaultColDef="defaultColDef"
              :gridOptions="payDocModalGridOptions"
              :modules="agGridModules"
              :pinnedBottomRowData="payDocModalPinnedBottomRowData"
              :getRowStyle="getRowStyle"
              rowSelection="single"
              @grid-ready="onPayDocModalGridReady"
              @first-data-rendered="onPayDocModalFirstDataRendered"
              @gridSizeChanged="onGridSizeChanged"
              @modelUpdated="onPayDocModalModelUpdated"
              @rowSelected="onPayDocModalRowSelected"
              @cellContextMenu="onPayDocModalRowRightClicked"
            ></ag-grid-vue>
          </el-col>
        </el-row>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-row type="flex" justify="space-between">
          <el-col :span="12" style="text-align:left;">
            <el-button
              v-if="postingModalBtnVisible"
              type="primary"
              size="small"
              @click="bankStatFormPosting"
              native-type="submit"
              v-shortkey="{ F9: ['f9'] }"
              @shortkey.native="onShortKeyRouter"
              ><span class="key-name">F9</span
              >{{ $t("common.buttons.posting") }}</el-button
            >
          </el-col>
          <el-col :span="12" style="text-align:right;">
            <el-button
              type="primary"
              size="small"
              @click="bankStatFormSubmit"
              native-type="submit"
              v-shortkey="{ F10: ['f10'] }"
              @shortkey.native="onShortKeyRouter"
              ><span class="key-name">F10</span
              >{{ $t("common.buttons.submit") }}</el-button
            >
            <el-button type="plain" size="small" @click="bankStatModal = false"
              ><span class="key-name">Esc</span
              >{{ $t("common.buttons.cancel") }}</el-button
            >
          </el-col>
        </el-row>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { formatSum, sortSum } from "@/helpers/reference";
import { mapGetters, mapActions } from "vuex";
import { AgGridVue } from "ag-grid-vue";
import { AllCommunityModules } from "@ag-grid-community/all-modules";
import agGridLocaleRu from "@/locales/ru/agGrid";
import agGridLocaleUa from "@/locales/ua/agGrid";
import agGridLocaleEn from "@/locales/en/agGrid";
import i18n from "@/locales";
import { $, gt } from "moneysafe";
import { setGridView } from "@/helpers/grid";
import { pickerOptions } from "@/mixins/pickerOptions";
import { dialogWidth } from "@/mixins/dialogWidth";
import { checkAccess } from "@/helpers/access";

export default {
  name: "bankpay",
  components: {
    AgGridVue
  },
  mixins: [pickerOptions, dialogWidth],
  data() {
    const validateDateRange = (rule, value, callback) => {
      if (this.filterForm.dateEnabled && !value) {
        callback(new Error(this.$t("common.validations.dateRange.message")));
      } else {
        callback();
      }
    };
    const validateSumRange = (rule, value, callback) => {
      if (
        this.filterForm.sumEnabled &&
        gt($(this.filterForm.sumBegin), $(this.filterForm.sumEnd))
      ) {
        callback(new Error(this.$t("common.validations.sumRange.message")));
      } else {
        callback();
      }
    };
    const validateStatementStatus = (rule, value, callback) => {
      if (this.filterForm.statementEnabled && !value) {
        callback(
          new Error(this.$t("common.validations.statementStatus.message"))
        );
      } else {
        callback();
      }
    };
    const validateDatebank = (rule, value, callback) => {
      const datebankConv = parseInt(this.$moment(value, "DD.MM.YYYY").unix());
      const dateClose = parseInt(
        this.$moment(
          this.params.tclosemonth.dateclose,
          "DD.MM.YYYY HH:mm:ss"
        ).unix()
      );
      const dateFee = parseInt(
        this.$moment(this.params.tclosemonth.datefee, "DD.MM.YYYY").unix()
      );
      const limitByCurrMonth = this.params.goptions.limitbycurrmonth;

      if (!value) {
        callback(new Error(this.$t("common.validations.datebank.message")));
      } else if (limitByCurrMonth === "yes" && datebankConv < dateFee) {
        callback(new Error(this.$t("common.validations.datebank.dateClose")));
      } else {
        callback();
      }
    };
    const validateDatepay = (rule, value, callback) => {
      const dateNow = parseInt(this.$moment().unix());
      const datepayConv = parseInt(this.$moment(value, "DD.MM.YYYY").unix());

      if (!value) {
        callback(new Error(this.$t("common.validations.datebank.message")));
      } else if (datepayConv > dateNow) {
        callback(new Error(this.$t("common.validations.datebank.dateNow")));
      } else {
        callback();
      }
    };
    const validateQuantity = (rule, value, callback) => {
      if (!this.uploadStatus && !value) {
        callback(new Error(this.$t("common.validations.quantity.message")));
      } else if (
        !this.uploadStatus &&
        this.postingStatus === "1" &&
        this.postingQuantity !== value
      ) {
        callback(new Error(this.$t("common.validations.quantity.status")));
      } else {
        callback();
      }
    };
    const validateSumma = (rule, value, callback) => {
      if (!this.uploadStatus && !value) {
        callback(new Error(this.$t("common.validations.summa.message")));
      } else if (
        !this.uploadStatus &&
        this.postingStatus === "1" &&
        this.postingSumma !== value
      ) {
        callback(new Error(this.$t("common.validations.summa.status")));
      } else {
        callback();
      }
    };
    const validateBeneficiaryAccount = (rule, value, callback) => {
      if (!value) {
        callback(
          new Error(this.$t("common.validations.beneficiaryAccount.message"))
        );
      } else {
        callback();
      }
    };
    const validatePaymentStatus = (rule, value, callback) => {
      if (!value) {
        callback(
          new Error(this.$t("common.validations.paymentStatus.message"))
        );
      } else {
        callback();
      }
    };
    const validateCodefirme = (rule, value, callback) => {
      if (this.payDocForm.paymentStatus === "0" && !value) {
        callback(new Error(this.$t("common.validations.codefirme.message")));
      } else {
        callback();
      }
    };
    const validatePayment = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$t("common.validations.payment.message")));
      } else {
        callback();
      }
    };
    const validateEncoding = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$t("common.validations.encoding.message")));
      } else {
        callback();
      }
    };
    const validateUploadFile = (rule, value, callback) => {
      if (this.$refs.upload.uploadFiles.length === 0) {
        callback(new Error(this.$t("common.validations.uploadFile.message")));
      } else if (
        !this.multiupload &&
        this.$refs.upload.uploadFiles.length > 1
      ) {
        callback(new Error(this.$t("common.validations.uploadFile.onlyOne")));
      } else {
        callback();
      }
    };
    const validateConfirmeCodefirme = (rule, value, callback) => {
      if (!this.confirmForm.alienpay && !value) {
        callback(
          new Error(this.$t("common.validations.confirmeCodefirme.message"))
        );
      } else {
        callback();
      }
    };
    return {
      agGridModules: AllCommunityModules,
      agGridlocaleText: null,
      bankStatGridOptions: null,
      bankStatGridApi: null,
      bankStatColumnApi: null,
      payDocGridOptions: null,
      payDocGridApi: null,
      payDocColumnApi: null,
      payDocPinnedBottomRowData: null,
      payDocModalGridOptions: null,
      payDocModalGridApi: null,
      payDocModalColumnApi: null,
      payDocModalPinnedBottomRowData: null,
      getRowStyle: null,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        autoHeight: false,
        filterParams: {
          applyButton: false,
          clearButton: true
        },
        headerClass: "ag-grid-header",
        cellClass: "ag-grid-cell"
      },
      bankStatColumnDefs: [
        {
          headerName: this.$t("bankpay.bankStatColumn.headers.bankStatement"),
          headerTooltip: this.$t(
            "bankpay.bankStatColumn.headers.bankStatement"
          ),
          children: [
            {
              headerName: this.$t("common.gridColumns.nameaccount"),
              headerTooltip: this.$t("common.gridColumns.nameaccount"),
              field: "nameaccount",
              tooltipField: "nameaccount",
              cellClass: "name-column"
            },
            {
              headerName: this.$t("common.gridColumns.datebank"),
              headerTooltip: this.$t("common.gridColumns.datebank"),
              field: "datebank",
              tooltipField: "datebank",
              sort: "asc",
              comparator: (date1, date2) =>
                parseInt(this.$moment(date1, "DD.MM.YYYY").unix()) -
                parseInt(this.$moment(date2, "DD.MM.YYYY").unix())
            },
            {
              headerName: this.$t("common.gridColumns.quantityPay"),
              headerTooltip: this.$t("common.gridColumns.quantityPay"),
              field: "quantity",
              tooltipField: "quantity",
              comparator: (value1, value2) =>
                parseInt(value1) - parseInt(value2)
            },
            {
              headerName: this.$t("common.gridColumns.totalSumma"),
              headerTooltip: this.$t("common.gridColumns.totalSumma"),
              field: "summa",
              tooltipField: "summa",
              cellClass: "sum-column",
              valueFormatter: params => formatSum(params.value),
              comparator: (sum1, sum2) => sortSum(sum1, sum2)
            },
            {
              headerName: this.$t("common.gridColumns.status"),
              headerTooltip: this.$t("common.gridColumns.status"),
              field: "status",
              tooltipValueGetter: params =>
                params.value === "1"
                  ? this.$t("common.types.status.posting")
                  : this.$t("common.types.status.inSet"),
              cellRenderer: params =>
                params.value === "1"
                  ? this.$t("common.types.status.posting")
                  : this.$t("common.types.status.inSet")
            }
          ]
        },
        {
          headerName: this.$t("bankpay.bankStatColumn.headers.postRecording"),
          headerTooltip: this.$t(
            "bankpay.bankStatColumn.headers.postRecording"
          ),
          children: [
            {
              headerName: this.$t("common.gridColumns.datebeginfact"),
              headerTooltip: this.$t("common.gridColumns.datebeginfact"),
              field: "datebeginfact",
              tooltipField: "datebeginfact",
              comparator: (date1, date2) =>
                parseInt(this.$moment(date1, "DD.MM.YYYY").unix()) -
                parseInt(this.$moment(date2, "DD.MM.YYYY").unix())
            },
            {
              headerName: this.$t("common.gridColumns.username"),
              headerTooltip: this.$t("common.gridColumns.username"),
              field: "username",
              tooltipField: "username",
              cellClass: "name-column"
            }
          ]
        }
      ],
      payDocColumnDefs: [
        {
          headerName: this.$t("bankpay.payDocColumn.headers.payDoc"),
          headerTooltip: this.$t("bankpay.payDocColumn.headers.payDoc"),
          children: [
            {
              headerName: this.$t("common.gridColumns.namefirmeSenderPay"),
              headerTooltip: this.$t("common.gridColumns.namefirmeSenderPay"),
              field: "namefirme",
              tooltipField: "namefirme",
              cellClass: "name-column"
            },
            {
              headerName: this.$t("common.gridColumns.okpo"),
              headerTooltip: this.$t("common.gridColumns.okpo"),
              field: "okpo",
              tooltipField: "okpo"
            },
            {
              headerName: this.$t("common.gridColumns.datepay"),
              headerTooltip: this.$t("common.gridColumns.datepay"),
              field: "datepay",
              tooltipField: "datepay",
              comparator: (date1, date2) =>
                parseInt(this.$moment(date1, "DD.MM.YYYY").unix()) -
                parseInt(this.$moment(date2, "DD.MM.YYYY").unix())
            },
            {
              headerName: this.$t("common.gridColumns.paynumber"),
              headerTooltip: this.$t("common.gridColumns.paynumber"),
              field: "paynumber",
              tooltipField: "paynumber"
            },
            {
              headerName: this.$t("common.gridColumns.summa"),
              headerTooltip: this.$t("common.gridColumns.summa"),
              field: "summa",
              tooltipField: "summa",
              cellClass: "sum-column",
              valueFormatter: params => formatSum(params.value),
              comparator: (sum1, sum2) => sortSum(sum1, sum2)
            },
            {
              headerName: this.$t("common.gridColumns.astatus"),
              headerTooltip: this.$t("common.gridColumns.astatus"),
              field: "astatus",
              tooltipValueGetter: params =>
                params.value === "1"
                  ? this.$t("common.types.astatus.yes")
                  : this.$t("common.types.astatus.no"),
              cellRenderer: params => {
                if (params.node.rowPinned) return "";
                return params.value === "1"
                  ? "<input type=checkbox checked disabled>"
                  : "<input type=checkbox disabled>";
              }
            },
            {
              headerName: this.$t("common.gridColumns.mess"),
              headerTooltip: this.$t("common.gridColumns.mess"),
              field: "mess",
              tooltipField: "mess",
              cellClass: "name-column"
            },
            {
              headerName: this.$t("common.gridColumns.npack"),
              headerTooltip: this.$t("common.gridColumns.npack"),
              field: "npack",
              tooltipField: "npack"
            }
          ]
        },
        {
          headerName: this.$t("bankpay.payDocColumn.headers.postRecording"),
          headerTooltip: this.$t("bankpay.payDocColumn.headers.postRecording"),
          children: [
            {
              headerName: this.$t("common.gridColumns.datebeginfact"),
              headerTooltip: this.$t("common.gridColumns.datebeginfact"),
              field: "datebeginfact",
              tooltipField: "datebeginfact",
              comparator: (date1, date2) =>
                parseInt(this.$moment(date1, "DD.MM.YYYY").unix()) -
                parseInt(this.$moment(date2, "DD.MM.YYYY").unix())
            },
            {
              headerName: this.$t("common.gridColumns.username"),
              headerTooltip: this.$t("common.gridColumns.username"),
              field: "username",
              tooltipField: "username",
              cellClass: "name-column"
            }
          ]
        }
      ],
      payDocModalColumnDefs: [
        {
          headerName: this.$t("common.gridColumns.npackStatus"),
          headerTooltip: this.$t("common.gridColumns.npackStatus"),
          field: "npack",
          tooltipValueGetter: params => {
            if (params.node.data.codefirme === "")
              return this.$t("common.types.npackStatus.alien");
            return params.value
              ? this.$t("common.types.npackStatus.associatedPack")
              : this.$t("common.types.npackStatus.expectedPack");
          },
          cellRenderer: params => {
            if (params.node.rowPinned) return this.currentPayDocModalRowsCount;
            if (params.node.data.codefirme === "")
              return this.$t("common.types.npackStatus.alien");
            return params.value
              ? this.$t("common.types.npackStatus.associatedPack")
              : this.$t("common.types.npackStatus.expectedPack");
          }
        },
        {
          headerName: this.$t("common.gridColumns.summa"),
          headerTooltip: this.$t("common.gridColumns.summa"),
          field: "summa",
          tooltipField: "summa",
          cellClass: "sum-column",
          valueFormatter: params => formatSum(params.value),
          comparator: (sum1, sum2) => sortSum(sum1, sum2)
        },
        {
          headerName: this.$t("common.gridColumns.namefirme"),
          headerTooltip: this.$t("common.gridColumns.namefirme"),
          field: "namefirme",
          tooltipField: "namefirme",
          cellClass: "name-column"
        },
        {
          headerName: this.$t("common.gridColumns.datepay"),
          headerTooltip: this.$t("common.gridColumns.datepay"),
          field: "datepay",
          tooltipField: "datepay",
          comparator: (date1, date2) =>
            parseInt(this.$moment(date1, "DD.MM.YYYY").unix()) -
            parseInt(this.$moment(date2, "DD.MM.YYYY").unix())
        },
        {
          headerName: this.$t("common.gridColumns.kindname"),
          headerTooltip: this.$t("common.gridColumns.kindname"),
          field: "kindname",
          tooltipField: "kindname"
        },
        {
          headerName: this.$t("common.gridColumns.okpoPay"),
          headerTooltip: this.$t("common.gridColumns.okpoPay"),
          field: "okpo",
          tooltipField: "okpo"
        },
        {
          headerName: this.$t("common.gridColumns.astatus"),
          headerTooltip: this.$t("common.gridColumns.astatus"),
          field: "astatus",
          sort: "asc",
          tooltipValueGetter: params =>
            params.value === "1"
              ? this.$t("common.types.astatus.yes")
              : this.$t("common.types.astatus.no"),
          cellRenderer: params => {
            if (params.node.rowPinned) return "";
            return params.value === "1"
              ? "<input type=checkbox checked disabled>"
              : "<input type=checkbox disabled>";
          }
        }
      ],
      filterModal: false,
      filterForm: {
        dateEnabled: null,
        dateRange: [],
        sumEnabled: null,
        sumBegin: 0,
        sumEnd: 0,
        statementEnabled: null,
        statementStatus: null
      },
      filterRules: {
        dateRange: [{ validator: validateDateRange, trigger: "blur" }],
        sumRange: [{ validator: validateSumRange, trigger: "blur" }],
        statementStatus: [
          { validator: validateStatementStatus, trigger: "blur" }
        ]
      },
      filterStatusOptions: [
        {
          value: "0",
          label: this.$t("common.filter.form.statementStatus.unexploded")
        },
        {
          value: "1",
          label: this.$t("common.filter.form.statementStatus.exploded")
        }
      ],
      filterType: "primary",
      paymentOptions: [
        {
          value: "0",
          label: this.$t("common.options.payment.zero")
        },
        {
          value: "1",
          label: this.$t("common.options.payment.one")
        }
      ],
      payBankForm: {
        datebank: "",
        quantity: 0,
        quantityDisabled: false,
        summa: 0,
        summaDisabled: false,
        statementid: "",
        datebeginfact: "",
        isimport: "",
        status: "",
        accountidCode: null,
        accountid: null,
        accounts: []
      },
      payBankRules: {
        datebank: [
          { required: true, validator: validateDatebank, trigger: "blur" }
        ],
        quantity: [
          { required: true, validator: validateQuantity, trigger: "blur" }
        ],
        summa: [{ required: true, validator: validateSumma, trigger: "blur" }],
        accountid: [
          {
            required: true,
            validator: validateBeneficiaryAccount,
            trigger: "blur"
          }
        ]
      },
      payDocForm: {
        addstatus: "",
        editstatus: "",
        statementid: "",
        paysheetid: "",
        paymentStatus: null,
        paynumber: "",
        okpo: "",
        datepay: "",
        kindpayCode: null,
        kindpay: null,
        mfo: "",
        codefirmeCode: null,
        codefirme: null,
        summa: 0,
        astatus: false,
        mess: "",
        accountid: "",
        datebank: "",
        datebeginfact: "",
        dateendfact: "",
        npack: "",
        userid: "",
        senders: [],
        kindpays: []
      },
      payDocRules: {
        paymentStatus: [
          { required: true, validator: validatePaymentStatus, trigger: "blur" }
        ],
        paynumber: [
          {
            required: true,
            message: this.$t("common.validations.paynumber.required"),
            trigger: "blur"
          }
        ],
        datepay: [
          { required: true, validator: validateDatepay, trigger: "blur" }
        ],
        codefirme: [
          { required: true, validator: validateCodefirme, trigger: "blur" }
        ],
        summa: [
          {
            required: true,
            message: this.$t("common.validations.summa.required"),
            trigger: "blur"
          }
        ],
        kindpay: [
          { required: true, validator: validatePayment, trigger: "blur" }
        ]
      },
      payDocSenderDisabled: false,
      payDocDatepayDisabled: false,
      payDocSummaDisabled: false,
      uploadFilesModal: false,
      multiupload: false,
      uploadStatus: false,
      uploadForm: {
        encoding: ""
      },
      uploadRules: {
        uploadfile: [
          { required: true, validator: validateUploadFile, trigger: "blur" }
        ],
        encoding: [
          { required: true, validator: validateEncoding, trigger: "blur" }
        ]
      },
      confirmModal: false,
      confirmForm: {
        name: null,
        comment: null,
        mfo: null,
        rcount: null,
        summa: 0,
        okpo: null,
        codefirme: null,
        codefirmeDisabled: false,
        kindpay: null,
        datepay: null,
        ndoc: null,
        organizations: [],
        alienpay: false,
        saveforsender: false
      },
      confirmRules: {
        codefirme: [
          {
            required: true,
            validator: validateConfirmeCodefirme,
            trigger: "blur"
          }
        ]
      },
      importData: null,
      importDataCurrentRow: null,
      confirmImportData: [],
      confirmAuto: [],
      money: {
        decimal: ".",
        thousands: " ",
        precision: 2
      },
      currentBankStatRowSelected: null,
      currentPayDocModalRowSelected: null,
      currentPayDocModalRowsCount: null,
      addBtnDisabled: false,
      editBtnDisabled: false,
      deleteBtnDisabled: false,
      importBtnDisabled: false,
      importModalBtnVisible: false,
      addModalBtnVisible: false,
      deleteModalBtnVisible: false,
      postingModalBtnVisible: false,
      bankStatementStatus: "",
      bankStatModal: false,
      editModalBtnDisabled: false,
      deleteModalBtnDisabled: false,
      alienModalBtnDisabled: false,
      payDocEditStatus: "",
      payDocEditModal: false,
      payBankLastRow: null,
      payDocModalLastRow: null,
      postingStatus: null,
      postingQuantity: null,
      postingSumma: null,
      fileMask: "",
      stornModal: false
    };
  },
  beforeCreate() {
    if (!checkAccess(75)) {
      this.$router.push("/");
    }
  },
  created() {
    document.title = this.$t("bankpay.title");
    this.getParams({
      account: "1",
      gfirme: "1",
      gkindpay: "1",
      gusers: "1",
      importcodepage: "1"
    });

    window.addEventListener("resize", this.onResize);
  },
  beforeMount() {
    const locale = i18n.locale;
    switch (locale) {
      case "ru":
        this.agGridlocaleText = agGridLocaleRu;
        break;
      case "ua":
        this.agGridlocaleText = agGridLocaleUa;
        break;
      case "en":
        this.agGridlocaleText = agGridLocaleEn;
        break;
      default:
        this.agGridlocaleText = agGridLocaleRu;
    }

    this.bankStatGridOptions = {
      suppressCellSelection: true,
      preventDefaultOnContextMenu: true,
      localeText: this.agGridlocaleText,
      rowClassRules: {
        "ag-state-delivered": params => params.data.status == "1",
        "ag-state-composing": params => params.data.status == "0"
      }
    };

    this.payDocGridOptions = {
      suppressCellSelection: true,
      preventDefaultOnContextMenu: true,
      localeText: this.agGridlocaleText
    };

    this.payDocModalGridOptions = {
      suppressCellSelection: true,
      preventDefaultOnContextMenu: true,
      localeText: this.agGridlocaleText,
      rowClassRules: {
        "ag-state-delivered": params =>
          params.data.npack && !params.node.rowPinned,
        "ag-state-composing": params =>
          !params.data.npack && !params.node.rowPinned
      }
    };

    this.getRowStyle = params => {
      if (params.node.rowPinned) return { "font-weight": "bold" };
    };

    let filter;
    const savedFilter = this.$localStorage.get("wb-bankpay-filter");
    if (savedFilter) {
      filter = JSON.parse(savedFilter);
      if (isNaN(Number(filter.sum.sumBegin))) filter.sum.sumBegin = "0.00";
      if (isNaN(Number(filter.sum.sumEnd))) filter.sum.sumEnd = "0.00";
    } else {
      const dateBegin = this.$moment()
        .subtract(1, "months")
        .format("DD.MM.YYYY");
      const dateEnd = this.$moment()
        .add(1, "months")
        .format("DD.MM.YYYY");
      filter = {
        period: {
          enabled: true,
          dateBegin: dateBegin,
          dateEnd: dateEnd
        },
        sum: {
          enabled: false,
          sumBegin: null,
          sumEnd: null
        },
        statement: {
          enabled: false,
          status: null
        }
      };
      this.$localStorage.set("wb-bankpay-filter", JSON.stringify(filter));
    }
    this.$store.commit("setBankPayFilter", filter);
  },
  computed: {
    ...mapGetters([
      "bankPayFilter",
      "bankStat",
      "payDoc",
      "payDocModal",
      "statusModifyBankStat",
      "statusUploadFile",
      "initPage",
      "params"
    ]),
    checkAllModal: function() {
      return (
        !this.filterModal &&
        !this.bankStatModal &&
        !this.payDocEditModal &&
        !this.uploadFilesModal &&
        !this.confirmModal &&
        !this.stornModal
      );
    }
  },
  watch: {
    "payBankForm.datebank": function(date) {
      if (date) {
        const currentDate = parseInt(this.$moment(date, "DD.MM.YYYY").unix());
        this.payBankForm.accounts = this.params.account.data.filter(
          item =>
            currentDate >=
              parseInt(this.$moment(item.datebegin, "DD.MM.YYYY").unix()) &&
            currentDate <=
              parseInt(this.$moment(item.dateend, "DD.MM.YYYY").unix())
        );
      } else {
        this.payBankForm.accountid = null;
        this.payBankForm.accounts = [];
      }
    },
    "payBankForm.accountid": function(value) {
      this.payBankForm.accountidCode = +value || null;
    },
    "payDocForm.paymentStatus": function(status) {
      if (status === "0") {
        this.payDocSenderDisabled = false;
      } else {
        this.payDocForm.mfo = "";
        this.payDocForm.codefirme = "";
        this.payDocSenderDisabled = true;
      }
    },
    "payDocForm.kindpay": function(value) {
      this.payDocForm.kindpayCode = +value || null;
    },
    "payDocForm.datepay": function(date) {
      if (date) {
        const currentDate = parseInt(this.$moment(date, "DD.MM.YYYY").unix());
        this.payDocForm.senders = this.params.gfirme.data.filter(
          item =>
            currentDate >=
              parseInt(this.$moment(item.datebegin, "DD.MM.YYYY").unix()) &&
            currentDate <=
              parseInt(this.$moment(item.dateend, "DD.MM.YYYY").unix()) &&
            (item.codegroup === "1" ||
              item.codegroup === "2" ||
              item.codegroup === "4")
        );
      } else {
        this.payDocForm.mfo = "";
        this.payDocForm.codefirme = null;
        this.payDocForm.senders = [];
      }
    },
    "payDocForm.codefirme": function(codefirme) {
      this.payDocForm.codefirmeCode = +codefirme || null;
      if (codefirme) {
        const gfirme = this.params.gfirme.data.filter(
          item => item.codefirme === codefirme
        );
        this.payDocForm.mfo = gfirme.length > 0 ? gfirme[0].mfo : "";
        if (gfirme.length > 0) {
          this.payDocForm.astatus = gfirme[0].status_prof === "1";
          this.payDocForm.kindpay = gfirme[0].kindpay;
        }
      } else {
        this.payDocForm.mfo = "";
      }
    },
    statusModifyBankStat: function(value) {
      if (value) {
        this.payBankLastRow = value.hasOwnProperty("statementid")
          ? value.statementid
          : null;
        this.bankStatModal = false;
        this.getBankStat();
      }
    },
    statusUploadFile: function(value) {
      if (value) {
        this.importData = {
          dbf: value.dbf.data,
          firm: value.firm.data,
          paramimp: this.params.goptions.paramimp
        };

        this.importDataCurrentRow = 0;
        this.checkImportData();
      }
    },
    "confirmForm.alienpay": function(value) {
      this.confirmForm.codefirmeDisabled = value ? true : false;
    }
  },
  beforeDestroy() {
    this.$store.commit("setBankPayFilter", {
      period: {
        enabled: false,
        dateBegin: "",
        dateEnd: ""
      },
      sum: {
        enabled: false,
        sumBegin: "",
        sumEnd: ""
      },
      statement: {
        enabled: false,
        status: ""
      }
    });
    this.$store.commit("setParams", null);
    this.$store.commit("clearBankStat");
    this.$store.commit("clearPayDoc");
    this.$store.commit("clearPayDocModal");

    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    ...mapActions(["getParams"]),
    onAddBtn() {
      this.bankStatementStatus = "insert";
      this.bankStatModal = true;
    },
    onEditBtn() {
      this.bankStatementStatus = "edit";
      this.bankStatModal = true;
    },
    onDeleteBtn() {
      this.stornModal = true;
      this.$confirm(
        this.$t("bankpay.deleteBankStat.message"),
        this.$t("bankpay.deleteBankStat.title"),
        {
          confirmButtonText: this.$t("common.buttons.confirmYes"),
          cancelButtonText: this.$t("common.buttons.confirmNo"),
          cancelButtonClass: "cancel-button",
          type: "warning"
        }
      )
        .then(() => {
          this.stornModal = false;
          const { statementid } = this.currentBankStatRowSelected;
          this.$store.commit("setStatusModifyBankStat", false);
          this.$store.dispatch("deleteBankStat", { statementid });
        })
        .catch(() => {
          this.stornModal = false;
        });
    },
    onImportBtn() {
      this.bankStatementStatus = "import";
      this.bankStatModal = true;
    },
    onGridSizeChanged(params) {
      // params.api.sizeColumnsToFit();
    },
    onBankStatGridReady(params) {
      this.bankStatGridApi = params.api;
      this.bankStatColumnApi = params.columnApi;

      this.getBankStat();
    },
    onBankStatFirstDataRendered(event) {
      setGridView("wb-bankpay-grid-bankstat", event, "optimal");
    },
    onBankStatRowRightClicked(event) {
      this.$store.commit("setGridContextMenuKey", "wb-bankpay-grid-bankstat");
      this.$store.commit("setGridContextMenuEvent", event);
      this.$store.commit("setGridContextMenuModal", true);
    },
    onBankStatRowSelected(params) {
      if (params.node.selected) {
        params.api.ensureIndexVisible(params.node.rowIndex);

        this.currentBankStatRowSelected = params.node.data;

        const {
          statementid,
          status,
          datebank
        } = this.currentBankStatRowSelected;

        const datebankConv = parseInt(
          this.$moment(datebank, "DD.MM.YYYY").unix()
        );
        const dateclose = parseInt(
          this.$moment(
            this.params.tclosemonth.dateclose,
            "DD.MM.YYYY HH:mm:ss"
          ).unix()
        );
        const statementFullEditEnabled = this.params.goptions
          .statementfulleditenabled;

        if (statementFullEditEnabled === "yes") {
          this.editBtnDisabled = false;
          this.deleteBtnDisabled = false;
        } else {
          if (datebankConv > dateclose) {
            this.editBtnDisabled = false;
            this.deleteBtnDisabled = false;
          } else if (datebankConv < dateclose && status === "0") {
            this.editBtnDisabled = false;
            this.deleteBtnDisabled = false;
          } else if (datebankConv < dateclose && status === "1") {
            this.editBtnDisabled = true;
            this.deleteBtnDisabled = true;
          }
        }

        if (this.payDocGridOptions.api) {
          this.payDocGridOptions.api.setRowData([]);
          this.payDocGridOptions.api.showLoadingOverlay();
        }
        this.$store.dispatch("getPayDoc", { statementid });
      }
    },
    onBankStatModelUpdated(params) {
      const rowCount = params.api.getDisplayedRowCount();

      this.filterType = rowCount >= 100 ? "danger" : "primary";

      if (rowCount === 0) {
        this.editBtnDisabled = true;
        this.deleteBtnDisabled = true;
      } else {
        this.editBtnDisabled = false;
        this.deleteBtnDisabled = false;
      }

      if (rowCount > 0) {
        params.api.forEachNode(node => {
          if (this.payBankLastRow) {
            node.setSelected(node.data.statementid === this.payBankLastRow);
          } else {
            node.rowIndex ? 0 : node.setSelected(true);
          }
        });
      }

      params.api.sizeColumnsToFit();
    },
    onPayDocGridReady(params) {
      this.payDocGridApi = params.api;
      this.payDocColumnApi = params.columnApi;
    },
    onPayDocFirstDataRendered(event) {
      setGridView("wb-bankpay-grid-paydoc", event, "optimal");
    },
    onPayDocRowRightClicked(event) {
      this.$store.commit("setGridContextMenuKey", "wb-bankpay-grid-paydoc");
      this.$store.commit("setGridContextMenuEvent", event);
      this.$store.commit("setGridContextMenuModal", true);
    },
    onPayDocModelUpdated(params) {
      const rowCount = params.api.getDisplayedRowCount();

      params.api.forEachNode(node =>
        node.rowIndex ? 0 : node.setSelected(true)
      );

      let pinnedRowSumma = 0;
      for (let i = 0; i < rowCount; i++) {
        let rowNode = params.api.getDisplayedRowAtIndex(i);
        pinnedRowSumma = $(pinnedRowSumma)
          .add($(rowNode.data.summa))
          .toNumber();
      }
      pinnedRowSumma = formatSum($(pinnedRowSumma).toFixed());

      try {
        this.payDocPinnedBottomRowData = [
          {
            namefirme: "",
            okpo: "",
            datepay: "",
            paynumber: rowCount,
            summa: pinnedRowSumma,
            astatus: "",
            mess: "",
            npack: "",
            datebeginfact: "",
            username: ""
          }
        ];
      } catch {
        this.payDocPinnedBottomRowData = [
          {
            namefirme: "",
            okpo: "",
            datepay: "",
            paynumber: "0",
            summa: "0.00",
            astatus: "",
            mess: "",
            npack: "",
            datebeginfact: "",
            username: ""
          }
        ];
      }

      params.api.sizeColumnsToFit();
    },
    onPayDocModalGridReady(params) {
      this.payDocModalGridApi = params.api;
      this.payDocModalColumnApi = params.columnApi;
    },
    onPayDocModalFirstDataRendered(event) {
      setGridView("wb-bankpay-grid-paydocmodal", event, "optimal");
    },
    onPayDocModalRowRightClicked(event) {
      this.$store.commit(
        "setGridContextMenuKey",
        "wb-bankpay-grid-paydocmodal"
      );
      this.$store.commit("setGridContextMenuEvent", event);
      this.$store.commit("setGridContextMenuModal", true);
    },
    onPayDocModalModelUpdated(params) {
      const rowCount = params.api.getDisplayedRowCount();
      this.currentPayDocModalRowsCount = rowCount;

      if (rowCount === 0) {
        this.editModalBtnDisabled = true;
        this.deleteModalBtnDisabled = true;
        this.alienModalBtnDisabled = true;
      } else {
        this.editModalBtnDisabled = false;
        this.deleteModalBtnDisabled = false;
        this.alienModalBtnDisabled = false;

        params.api.forEachNode(node =>
          node.rowIndex ? 0 : node.setSelected(true)
        );
      }

      let pinnedRowSumma = 0;
      for (let i = 0; i < rowCount; i++) {
        let rowNode = params.api.getDisplayedRowAtIndex(i);
        pinnedRowSumma = $(pinnedRowSumma)
          .add($(rowNode.data.summa))
          .toNumber();
      }

      this.postingQuantity = rowCount;
      this.postingSumma = pinnedRowSumma;

      pinnedRowSumma = formatSum($(pinnedRowSumma).toFixed());

      try {
        params.api.forEachNode(node => {
          if (this.payDocModalLastRow) {
            node.setSelected(node.data.paysheetid == this.payDocModalLastRow);
          } else {
            node.rowIndex ? 0 : node.setSelected(true);
          }
        });

        this.payDocModalPinnedBottomRowData = [
          {
            npack: rowCount,
            summa: pinnedRowSumma,
            namefirme: "",
            datepay: "",
            kindname: "",
            okpo: "",
            astatus: ""
          }
        ];
      } catch {
        this.payDocModalPinnedBottomRowData = [
          {
            npack: "0",
            summa: "0.00",
            namefirme: "",
            datepay: "",
            kindname: "",
            okpo: "",
            astatus: ""
          }
        ];
      }

      params.api.sizeColumnsToFit();
    },
    onPayDocModalRowSelected(params) {
      if (params.node.selected) {
        params.api.ensureIndexVisible(params.node.rowIndex);

        this.currentPayDocModalRowSelected = params.node.data;

        const { codefirme, npack } = this.currentPayDocModalRowSelected;

        this.editModalBtnDisabled = npack ? true : false;
        this.deleteModalBtnDisabled = npack ? true : false;
        this.alienModalBtnDisabled = !codefirme || npack ? true : false;
      }
    },
    onImportModalDbfBtn() {
      this.fileMask = " .dbf, .exp";
      this.onImportModalBtn();
    },
    onImportModalCsvBtn() {
      this.fileMask = " .csv";
      this.onImportModalBtn();
    },
    onImportModalBtn() {
      this.uploadStatus = true;

      this.$refs.payBankForm.validate(valid => {
        if (valid) {
          this.uploadFilesModal = true;
        }
      });
    },
    onAddModalBtn() {
      this.payDocEditStatus = "insert";
      this.payDocEditModal = true;
    },
    onEditModalBtn() {
      this.payDocEditStatus = "edit";
      this.payDocEditModal = true;
    },
    onDeletModalBtn() {
      this.$confirm(
        this.$t("bankpay.deleteModal.message"),
        this.$t("bankpay.deleteModal.title"),
        {
          confirmButtonText: this.$t("common.buttons.confirmYes"),
          cancelButtonText: this.$t("common.buttons.confirmNo"),
          cancelButtonClass: "cancel-button",
          type: "warning"
        }
      )
        .then(() => {
          const { paysheetid, addstatus } = this.currentPayDocModalRowSelected;
          const deleteRow = {
            paysheetid,
            addstatus
          };
          this.payDocModalLastRow = null;

          this.$store.commit("deletePayDocModal", deleteRow);
        })
        .catch(() => {});
    },
    onAlienModalBtn() {
      const modifyedRow = Object.assign({}, this.currentPayDocModalRowSelected);
      modifyedRow.codefirme = "";
      modifyedRow.editstatus = "1";
      this.payDocModalLastRow = modifyedRow.paysheetid;

      this.$store.commit("editPayDocModal", modifyedRow);
    },
    onChangeMfo(value) {
      if (value) {
        const gfirme = this.params.gfirme.data.filter(
          item => item.mfo === value
        );
        if (gfirme.length > 0 && this.payDocForm.datepay) {
          this.payDocForm.codefirme = gfirme[0].codefirme;
        }
      } else {
        this.payDocForm.codefirme = null;
      }
    },
    payDocFormAdd() {
      this.$refs.payDocForm.validate(valid => {
        if (valid) {
          this.addPayDocModal();

          this.payDocForm.paysheetid = this.$uuid.v4();
          this.payDocForm.paynumber = "";
          this.payDocForm.okpo = "";
          this.payDocForm.codefirme = null;
          this.payDocForm.summa = "";
          this.payDocForm.astatus = false;
          this.payDocForm.mess = "";

          this.payDocModalLastRow = this.payDocForm.paysheetid;
        }
      });
    },
    payDocFormSubmit() {
      this.$refs.payDocForm.validate(valid => {
        if (valid) {
          if (this.payDocEditStatus === "insert") {
            this.payDocModalLastRow = this.payDocForm.paysheetid;

            this.addPayDocModal();
          } else if (this.payDocEditStatus === "edit") {
            let modifyedRow = Object.assign({}, this.payDocForm);
            modifyedRow.astatus = modifyedRow.astatus ? "1" : "0";
            modifyedRow.summa = $(modifyedRow.summa).toFixed();
            delete modifyedRow.paymentStatus;
            delete modifyedRow.mfo;
            delete modifyedRow.senders;
            delete modifyedRow.kindpays;
            this.payDocModalLastRow = modifyedRow.paysheetid;

            this.$store.commit("editPayDocModal", modifyedRow);
          }
          this.payDocEditModal = false;
        }
      });
    },
    addPayDocModal() {
      const {
        addstatus,
        editstatus,
        statementid,
        paysheetid,
        paynumber,
        okpo,
        datepay,
        codefirme,
        summa,
        astatus,
        kindpay,
        mess,
        accountid,
        datebank,
        datebeginfact,
        dateendfact,
        npack,
        userid
      } = this.payDocForm;

      const payload = {
        addstatus,
        editstatus,
        statementid,
        paysheetid,
        paynumber,
        okpo,
        datepay,
        codefirme,
        summa: $(summa).toFixed(),
        astatus: astatus ? "1" : "0",
        kindpay,
        mess,
        accountid,
        datebank,
        datebeginfact,
        dateendfact,
        npack,
        userid
      };

      this.$store.commit("addPayDocModal", payload);
    },
    getBankStat() {
      if (this.bankStatGridOptions.api) {
        this.payDocGridOptions.api.setRowData([]);

        this.bankStatGridOptions.api.setRowData([]);
        this.bankStatGridOptions.api.showLoadingOverlay();
      }
      this.$store.dispatch("getBankStat");
    },
    checkImportData() {
      const totalImportDbfDataRows = this.importData.dbf.length;

      if (this.importDataCurrentRow < totalImportDbfDataRows) {
        // начало: заполнение данных для формы соответствия 'Организация-отправитель'
        this.confirmForm.name = this.importData.dbf[
          this.importDataCurrentRow
        ].sendername;
        this.confirmForm.comment = this.importData.dbf[
          this.importDataCurrentRow
        ].comment;
        this.confirmForm.summa = $(
          this.importData.dbf[this.importDataCurrentRow].summa
        ).toNumber();
        this.confirmForm.rcount = this.importData.dbf[
          this.importDataCurrentRow
        ].senderrcount;
        this.confirmForm.mfo = this.importData.dbf[
          this.importDataCurrentRow
        ].sendermfo;
        this.confirmForm.okpo = this.importData.dbf[
          this.importDataCurrentRow
        ].okpo;
        this.confirmForm.datepay = this.importData.dbf[
          this.importDataCurrentRow
        ].datepay;
        this.confirmForm.ndoc = this.importData.dbf[
          this.importDataCurrentRow
        ].ndoc;
        this.confirmForm.codefirme = "";
        this.confirmForm.codefirmeDisabled = false;
        this.confirmForm.kindpay = "1";
        this.confirmForm.alienpay = false;
        this.confirmForm.saveforsender = false;

        let confirmAuto = [];
        switch (this.importData.paramimp) {
          // искать получателя по МФО
          case "0":
            confirmAuto = this.confirmAuto.filter(
              item =>
                item.dbfRcount === this.confirmForm.rcount &&
                item.dbfMfo === this.confirmForm.mfo
            );
            break;
          // искать получателя по ОКПО
          case "1":
            confirmAuto = this.confirmAuto.filter(
              item =>
                item.dbfRcount === this.confirmForm.rcount &&
                item.dbfOkpo === this.confirmForm.okpo
            );
            break;
          // искать получателя по МФО и ОКПО
          case "2":
            confirmAuto = this.confirmAuto.filter(
              item =>
                item.dbfRcount === this.confirmForm.rcount &&
                item.dbfMfo === this.confirmForm.mfo &&
                item.dbfOkpo === this.confirmForm.okpo
            );
            break;
          // искать получателя по МФО или ОКПО
          case "3":
            confirmAuto = this.confirmAuto.filter(
              item =>
                (item.dbfRcount === this.confirmForm.rcount &&
                  item.dbfMfo === this.confirmForm.mfo) ||
                (item.dbfRcount === this.confirmForm.rcount &&
                  item.dbfOkpo === this.confirmForm.okpo)
            );
            break;
        }

        if (confirmAuto.length > 0) {
          this.confirmForm.codefirme = confirmAuto[0].firmCodefirme;
          this.confirmForm.kindpay = confirmAuto[0].firmKindpay;
          this.confirmForm.okpo = confirmAuto[0].firmOkpo;

          this.saveImportData();
          this.importDataCurrentRow += 1;
          this.checkImportData();
        } else {
          const paysheetid = this.importData.dbf[this.importDataCurrentRow]
            .paysheetid;
          const firms = this.importData.firm.filter(
            item => item.paysheetid === paysheetid
          );
          if (firms.length === 0) {
            this.saveImportData();
            this.importDataCurrentRow += 1;
            this.checkImportData();
          } else if (firms.length === 1) {
            this.confirmForm.okpo = firms[0].okpo;
            this.confirmForm.codefirme = firms[0].codefirme;
            this.confirmForm.kindpay = firms[0].kindpay || "1";

            this.saveImportData();
            this.importDataCurrentRow += 1;
            this.checkImportData();
          } else {
            firms.forEach(firm => {
              const gfirme = this.params.gfirme.data.filter(
                item => item.codefirme === firm.codefirme
              );
              if (gfirme.length > 0) {
                const organization = {
                  codefirme: gfirme[0].codefirme,
                  namefirme: `${gfirme[0].namefirme}, ${this.$t(
                    "bankpay.checkImport.rcount"
                  )} - ${gfirme[0].rcount}`
                };
                this.confirmForm.organizations.push(organization);
              }
            });
            this.confirmForm.codefirme = this.confirmForm.organizations[0].codefirme;

            this.confirmModal = true;
          }
        }
      } else {
        this.confirmModal = false;
        this.$store.commit("importPayDocModal", this.confirmImportData);

        if (this.importData.dbf.length > 0) {
          this.payBankForm.datebank = this.importData.dbf[0].datebank;

          // финал: подсчет кол-ва и итоговой суммы, запись их в поля формы 'Выписка банка', очистка
          let totalRowSumma = 0;
          this.confirmImportData.forEach(item => {
            totalRowSumma = $(totalRowSumma)
              .add($(item.summa))
              .toNumber();
          });
          this.payBankForm.quantity = this.confirmImportData.length;
          this.payBankForm.summa = $(totalRowSumma).toNumber();
        }

        this.confirmForm = {
          name: null,
          comment: null,
          mfo: null,
          rcount: null,
          summa: 0,
          okpo: null,
          codefirme: null,
          codefirmeDisabled: false,
          kindpay: null,
          datepay: null,
          ndoc: null,
          organizations: [],
          alienpay: false,
          saveforsender: false
        };

        this.confirmAuto = [];
        this.confirmImportData = [];
        this.importData = null;
        this.importDataCurrentRow = null;
      }
    },
    saveImportData() {
      const importPayDocModal = {
        addstatus: "1",
        editstatus: "",
        statementid: this.payBankForm.statementid,
        paysheetid: this.$uuid.v4(),
        paynumber: this.confirmForm.ndoc,
        okpo: this.confirmForm.okpo,
        datepay: this.confirmForm.datepay,
        codefirme: this.confirmForm.codefirme,
        summa: $(this.confirmForm.summa).toFixed(),
        astatus: "1",
        kindpay: this.confirmForm.kindpay,
        mess: this.confirmForm.comment,
        accountid: "",
        datebank: "",
        datebeginfact: "",
        dateendfact: "",
        npack: "",
        userid: ""
      };

      // сохранение данных с установленным соответствием во временный массив
      this.confirmImportData.push(importPayDocModal);

      this.confirmForm = {
        name: null,
        comment: null,
        mfo: null,
        rcount: null,
        summa: 0,
        okpo: null,
        codefirme: null,
        codefirmeDisabled: false,
        kindpay: null,
        datepay: null,
        ndoc: null,
        organizations: [],
        alienpay: false,
        saveforsender: false
      };
    },
    filterModalShow() {
      const dateBegin = this.bankPayFilter.period.dateBegin;
      const dateEnd = this.bankPayFilter.period.dateEnd;
      this.filterForm = {
        dateEnabled: this.bankPayFilter.period.enabled,
        dateRange: [dateBegin, dateEnd],
        sumEnabled: this.bankPayFilter.sum.enabled,
        sumBegin: $(this.bankPayFilter.sum.sumBegin).toNumber(),
        sumEnd: $(this.bankPayFilter.sum.sumEnd).toNumber(),
        statementEnabled: this.bankPayFilter.statement.enabled,
        statementStatus: this.bankPayFilter.statement.status
      };
    },
    filterModalHide() {
      this.filterForm = {
        dateEnabled: null,
        dateRange: [],
        sumEnabled: null,
        sumBegin: 0,
        sumEnd: 0,
        statementEnabled: null,
        statementStatus: null
      };
      this.$refs.filterForm.resetFields();
    },
    filterModalSubmit() {
      this.$refs.filterForm.validate(valid => {
        if (valid) {
          const filter = {
            period: {
              enabled: this.filterForm.dateEnabled,
              dateBegin: this.filterForm.dateRange[0],
              dateEnd: this.filterForm.dateRange[1]
            },
            sum: {
              enabled: this.filterForm.sumEnabled,
              sumBegin: this.filterForm.sumBegin
                ? $(this.filterForm.sumBegin).toFixed()
                : "0.00",
              sumEnd: this.filterForm.sumEnd
                ? $(this.filterForm.sumEnd).toFixed()
                : "0.00"
            },
            statement: {
              enabled: this.filterForm.statementEnabled,
              status: this.filterForm.statementStatus
            }
          };
          this.$localStorage.set("wb-bankpay-filter", JSON.stringify(filter));
          this.$store.commit("setBankPayFilter", filter);
          this.filterModal = false;

          this.$store.commit("clearPayDoc");
          this.getBankStat();
        }
      });
    },
    bankStatModalShow() {
      if (this.bankStatementStatus === "insert") {
        this.payBankForm = {
          datebank: "",
          quantity: 0,
          quantityDisabled: false,
          summa: 0,
          summaDisabled: false,
          statementid: "",
          datebeginfact: "",
          isimport: "0",
          status: "",
          accountidCode: null,
          accountid: null,
          accounts: []
        };

        this.importModalBtnVisible = false;
        this.addModalBtnVisible = this.deleteModalBtnVisible = true;
        this.postingModalBtnVisible = true;

        this.payDocDatepayDisabled = this.payDocSummaDisabled = false;

        this.$store.commit("clearPayDocModal");
      } else if (this.bankStatementStatus === "edit") {
        const {
          datebank,
          quantity,
          summa,
          accountid,
          isimport,
          status,
          statementid,
          datebeginfact
        } = this.currentBankStatRowSelected;

        const payDocModal = {
          data: [...this.payDoc],
          delete: []
        };

        this.payBankForm = {
          datebank,
          quantity,
          summa: $(summa).toNumber(),
          statementid,
          datebeginfact,
          isimport,
          status,
          accountid,
          accounts: []
        };

        this.payBankForm.quantityDisabled = this.payBankForm.summaDisabled =
          isimport === "1" ? true : false;

        this.payDocDatepayDisabled = this.payDocSummaDisabled =
          isimport === "1" ? true : false;

        this.importModalBtnVisible = false;

        this.addModalBtnVisible = this.deleteModalBtnVisible =
          isimport === "1" ? false : true;

        this.postingModalBtnVisible = status === "1" ? false : true;

        this.$store.commit("setPayDocModal", payDocModal);
      } else if (this.bankStatementStatus === "import") {
        this.payBankForm = {
          datebank: "",
          quantity: 0,
          quantityDisabled: true,
          summa: 0,
          summaDisabled: true,
          statementid: "",
          datebeginfact: "",
          isimport: "1",
          status: "",
          accountid: null,
          accounts: []
        };

        this.importModalBtnVisible = true;
        this.addModalBtnVisible = this.deleteModalBtnVisible = false;
        this.postingModalBtnVisible = true;

        this.payDocDatepayDisabled = this.payDocSummaDisabled = true;

        this.$store.commit("clearPayDocModal");
      }
    },
    bankStatModalHide() {
      this.bankStatementStatus = "";
      this.currentPayDocModalRowSelected = null;
      this.currentPayDocModalRowsCount = null;
      this.payDocModalLastRow = null;
      this.postingStatus = this.postingQuantity = this.postingSumma = null;
      this.payBankForm = {
        datebank: "",
        quantity: 0,
        quantityDisabled: false,
        summa: 0,
        summaDisabled: false,
        statementid: "",
        datebeginfact: "",
        isimport: "",
        status: "",
        accountidCode: null,
        accountid: null,
        accounts: []
      };
      this.$store.commit("clearPayDocModal");
      this.$refs.payBankForm.resetFields();
    },
    bankStatFormPosting() {
      this.uploadStatus = false;
      this.postingStatus = "1";

      this.$refs.payBankForm.validate(valid => {
        if (valid) {
          this.$confirm(
            this.$t("bankpay.bankStatPostingConfirm.message"),
            this.$t("bankpay.bankStatPostingConfirm.title"),
            {
              confirmButtonText: this.$t("common.buttons.confirmYes"),
              cancelButtonText: this.$t("common.buttons.confirmNo"),
              cancelButtonClass: "cancel-button",
              type: "info"
            }
          )
            .then(() => {
              this.bankStatPayDocEdit("1");
            })
            .catch(() => {});
        }
      });
    },
    bankStatFormSubmit() {
      this.uploadStatus = false;
      this.postingStatus = this.payBankForm.status === "1" ? "1" : "0";

      this.$refs.payBankForm.validate(valid => {
        if (valid) {
          this.bankStatPayDocEdit("0");
        }
      });
    },
    bankStatPayDocEdit(inputStatus) {
      const {
        datebank,
        quantity,
        accountid,
        statementid,
        datebeginfact,
        isimport
      } = this.payBankForm;
      const summa = $(this.payBankForm.summa).toFixed();
      const status =
        this.bankStatementStatus === "edit" && inputStatus === "0"
          ? this.payBankForm.status
          : inputStatus;

      this.$store.commit("setStatusModifyBankStat", false);
      this.$store.dispatch("bankStatPayDocEdit", {
        datebank,
        quantity,
        accountid,
        statementid,
        datebeginfact,
        summa,
        isimport,
        status
      });
    },
    payDocEditModalShow() {
      if (this.payDocEditStatus === "insert") {
        this.payDocForm = {
          addstatus: "1",
          editstatus: "",
          statementid: this.payBankForm.statementid,
          paysheetid: this.$uuid.v4(),
          paymentStatus: "0",
          paynumber: "",
          okpo: "",
          datepay: this.payBankForm.datebank,
          kindpayCode: null,
          kindpay: null,
          mfo: "",
          codefirmeCode: null,
          codefirme: null,
          summa: 0,
          astatus: false,
          mess: "",
          accountid: "",
          datebank: "",
          datebeginfact: "",
          dateendfact: "",
          npack: "",
          userid: "",
          senders: [],
          kindpays: []
        };
      } else if (this.payDocEditStatus === "edit") {
        const {
          addstatus,
          statementid,
          paysheetid,
          paynumber,
          okpo,
          datepay,
          kindpay,
          codefirme,
          summa,
          astatus,
          mess,
          accountid,
          datebank,
          datebeginfact,
          dateendfact,
          npack,
          userid
        } = this.currentPayDocModalRowSelected;

        this.payDocForm = {
          addstatus,
          editstatus: "1",
          statementid,
          paysheetid,
          paymentStatus: codefirme === "" ? "1" : "0",
          paynumber,
          okpo,
          datepay,
          kindpay,
          mfo: "",
          codefirme,
          summa: $(summa).toNumber(),
          astatus: astatus === "1" ? true : false,
          mess,
          accountid,
          datebank,
          datebeginfact,
          dateendfact,
          npack,
          userid,
          senders: [],
          kindpays: []
        };
      }

      this.payDocForm.kindpays = this.params.gkindpay.data.filter(
        item => item.used === "1"
      );
    },
    payDocEditModalHide() {
      this.payDocEditStatus = "";
      this.payDocForm = {
        addstatus: "",
        editstatus: "",
        statementid: "",
        paysheetid: "",
        paymentStatus: null,
        paynumber: "",
        okpo: "",
        datepay: "",
        kindpayCode: null,
        kindpay: null,
        mfo: "",
        codefirmeCode: null,
        codefirme: null,
        summa: 0,
        astatus: false,
        mess: "",
        accountid: "",
        datebank: "",
        datebeginfact: "",
        dateendfact: "",
        npack: "",
        userid: "",
        senders: [],
        kindpays: []
      };
      this.$refs.payDocForm.resetFields();
    },
    uploadFilesModalShow() {
      this.uploadForm.encoding = "CP1251";
    },
    uploadFilesModalHide() {
      this.uploadStatus = false;
      this.uploadForm.encoding = "";
      this.fileMask = "";
      this.$refs.upload.clearFiles();
      this.$refs.uploadForm.resetFields();
    },
    uploadFilesSubmit() {
      this.$refs.uploadForm.validate(valid => {
        if (valid) {
          const accounts = this.payBankForm.accounts.filter(
            item => item.accountid === this.payBankForm.accountid
          );
          const account = accounts.length > 0 ? accounts[0].rcount : "";
          const encoding = this.uploadForm.encoding;
          const file = this.$refs.upload.uploadFiles[0];

          this.uploadFilesModal = false;

          this.$store.commit("setStatusUploadFile", null);
          this.$store.dispatch("uploadFile", { account, encoding, file });
        }
      });
    },
    confirmModalShow() {
      //this.confirmImportData = [];
    },
    confirmModalHide() {
      this.$refs.confirmForm.resetFields();
    },
    confirmFormSubmit() {
      this.$refs.confirmForm.validate(valid => {
        if (valid) {
          // если на форме отмечен 'Чужой платеж'
          if (this.confirmForm.alienpay) {
            this.confirmForm.okpo = this.importData.dbf[
              this.importDataCurrentRow
            ].okpo;
            this.confirmForm.codefirme = "";
          } else {
            const paysheetid = this.importData.dbf[this.importDataCurrentRow]
              .paysheetid;
            const firms = this.importData.firm.filter(
              item =>
                item.paysheetid === paysheetid &&
                item.codefirme === this.confirmForm.codefirme
            );
            if (firms.length > 0) {
              this.confirmForm.okpo = firms[0].okpo;
              this.confirmForm.kindpay = firms[0].kindpay || "1";
            }
          }

          // если на форме отмечен 'Запомнить выбор для данного отправителя'
          if (this.confirmForm.saveforsender) {
            const save = {
              dbfRcount: this.importData.dbf[this.importDataCurrentRow]
                .senderrcount,
              dbfMfo: this.importData.dbf[this.importDataCurrentRow].sendermfo,
              dbfOkpo: this.importData.dbf[this.importDataCurrentRow].okpo,
              firmCodefirme: this.confirmForm.codefirme,
              firmKindpay: this.confirmForm.kindpay,
              firmOkpo: this.confirmForm.okpo
            };
            this.confirmAuto.push(save);
          }

          this.saveImportData();
          this.importDataCurrentRow += 1;

          this.confirmModal = false;

          this.checkImportData();
        }
      });
    },
    confirmFormCancel() {
      this.confirmForm = {
        name: null,
        comment: null,
        mfo: null,
        rcount: null,
        summa: 0,
        okpo: null,
        codefirme: null,
        codefirmeDisabled: false,
        kindpay: null,
        datepay: null,
        ndoc: null,
        organizations: [],
        alienpay: false,
        saveforsender: false
      };

      this.confirmAuto = [];
      this.confirmImportData = [];
      this.importData = null;
      this.importDataCurrentRow = null;

      this.confirmModal = false;
    },
    onChangeAccountidCode(event) {
      const value = event.target.value;
      const accounts = this.payBankForm.accounts.filter(
        item => item.accountid === value
      );
      if (accounts.length > 0) {
        this.payBankForm.accountid = value;
      } else {
        this.payBankForm.accountid = null;
        this.payBankForm.accountidCode = null;
      }
    },
    onChangeKindpayCode(event) {
      const value = event.target.value;
      const kindpays = this.payDocForm.kindpays.filter(
        item => item.kindpay === value
      );
      if (kindpays.length > 0) {
        this.payDocForm.kindpay = value;
      } else {
        this.payDocForm.kindpay = null;
        this.payDocForm.kindpayCode = null;
      }
    },
    onChangeCodefirmeCode(event) {
      const value = event.target.value;
      const codefirmes = this.payDocForm.senders.filter(
        item => item.codefirme === value
      );
      if (codefirmes.length > 0) {
        this.payDocForm.codefirme = value;
      } else {
        this.payDocForm.codefirme = null;
        this.payDocForm.codefirmeCode = null;
      }
    },
    onShortKeyRouter(event) {
      switch (event.srcKey) {
        case "F2":
          // payDocEditModal: switch type field Pay
          if (this.payDocEditModal) {
            this.payDocForm.paymentStatus =
              this.payDocForm.paymentStatus === "0" ? "1" : "0";
          }
          break;
        case "F3":
          // payDocEditModal: kom sbor
          if (this.payDocEditModal) {
            this.payDocForm.astatus = !this.payDocForm.astatus;
          }
          break;
        case "F4":
        case "Star":
          // edit: F4, Star
          if (this.checkAllModal && !this.editBtnDisabled) {
            this.onEditBtn();
          }
          // bankStatModal: F4, Star
          else if (
            this.bankStatModal &&
            !this.payDocEditModal &&
            !this.editModalBtnDisabled
          ) {
            this.onEditModalBtn();
          }
          break;
        case "F7":
          // filterModal
          if (this.checkAllModal) {
            this.filterModal = true;
          }
          break;
        case "F8":
          // import
          if (this.checkAllModal && !this.importBtnDisabled) {
            this.onImportBtn();
          }
          break;
        case "F9":
          // posting: bankStatPosting
          if (this.bankStatModal && !this.payDocEditModal) {
            this.bankStatFormPosting();
          }
          break;
        case "F10":
          // submit: filterModalSubmit
          if (this.filterModal) {
            this.filterModalSubmit();
          }
          // submit: bankStatSubmit
          else if (this.bankStatModal && !this.payDocEditModal) {
            this.bankStatFormSubmit();
          }
          // submit: payDocEditSubmit
          else if (this.payDocEditModal) {
            this.payDocFormSubmit();
          }
          break;
        case "Insert":
        case "Plus":
          // Insert, Plus
          if (this.checkAllModal && !this.addBtnDisabled) {
            this.onAddBtn();
          }
          // bankStatModal: Insert, Plus
          else if (
            this.bankStatModal &&
            !this.payDocEditModal &&
            this.addModalBtnVisible
          ) {
            this.onAddModalBtn();
          }
          // posting: payDocEdit
          else if (this.payDocEditModal && this.payDocEditStatus === "insert") {
            this.payDocFormAdd();
          }
          break;
        case "Del":
        case "Minus":
          // delete: Del, Minus
          if (this.checkAllModal && !this.deleteBtnDisabled) {
            this.onDeleteBtn();
          }
          // bankStatModal: Del, Minus
          else if (
            this.bankStatModal &&
            !this.payDocEditModal &&
            this.deleteModalBtnVisible &&
            !this.deleteModalBtnDisabled
          ) {
            this.onDeletModalBtn();
          }
          break;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.grid-container {
  margin-top: 10px;
  height: calc(50vh - 93px);
  overflow-y: auto;
}

.grid-container-modal {
  margin-top: 20px;
  height: calc(30vh);
  overflow-y: auto;
}

.el-button--primary {
  font-weight: 500 !important;
  color: rgb(49, 49, 78) !important;
  font-family: "Open Sans Bold", sans-serif !important;
}

.ag-header-icon .ag-header-cell-sorted-asc {
  color: green !important;
}

.moneydisable {
  background-color: #f5f7fa;
  border-color: #e4e7ed;
  color: #c0c4cc;
  cursor: not-allowed;
  &:hover {
    border-color: #e4e7ed;
  }
}

.required {
  margin-left: 5px;
  color: red;
}

.button-group {
  margin-right: 10px;
}
</style>

<style lang="scss">
.el-input-number .el-input__inner {
  text-align: left;
}
</style>
